import { Directive, ElementRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UnsubscribeService } from '@app/infrastructure/unsubscribe/unsubscribe.service';

@Directive({
	selector: '[appCounter]',
	providers: [UnsubscribeService],
})
export class CounterDirective implements OnChanges {
	@Input() public appCounter: string;

	private beginInnerHTML = '';
	private readonly directiveClass = 'appCounter';
	private readonly defaultTransitionDuration = 1.0; // seconds
	private readonly defaultAnimationDelay = 0; // ms
	private readonly defaultAnimatedDigitOffset = 3;

	constructor(private elementRef: ElementRef) {}

	public ngOnChanges(changes: SimpleChanges): void {
		if (!changes?.appCounter?.currentValue) {
			return;
		}
		this.beginInnerHTML = this.appCounter;
		this.transform(this.elementRef.nativeElement);
	}

	private transform(counterWrapper: HTMLElement): void {
		counterWrapper.textContent = '';
		counterWrapper.classList.add(this.directiveClass);

		const content = this.trimAndClearNewLines(this.beginInnerHTML);
		const chunks = this.splitNumbersAndNonNumbers(content);

		this.insertHTML(counterWrapper, chunks);

		const animatedElementsNodeList = counterWrapper.querySelectorAll('span[data-value]');
		const animatedElements: HTMLElement[] = Array.prototype.slice.call(animatedElementsNodeList);

		setTimeout(() => this.runAnimation(animatedElements), this.defaultAnimationDelay);
	}

	// Insert static & dynamic content into HTML wrapper
	private insertHTML(wrapper: HTMLElement, chunks: string[]): void {
		for (let i = 0; i < chunks.length; i++) {
			const chunk = chunks[i];
			const lastIndex = chunks.length - 1;

			if (isNaN(Number(chunk)) || i !== lastIndex) {
				// Insert static content
				wrapper.insertAdjacentHTML('beforeend', `<span>${chunk}</span>`);
			} else {
				for (let k = 0; k < chunk.length; k++) {
					const digit = chunk[k];
					// Insert dynamic content
					wrapper.insertAdjacentHTML(
						'beforeend',
						`<span data-value="${digit}">${this.generateAnimatedHTML(digit)}</span>`,
					);
				}
			}
		}
	}

	private runAnimation(elements: HTMLElement[]): void {
		let i = 1;

		for (let element of elements) {
			const transitionDuration = this.defaultTransitionDuration + i * 0.3;
			const dist = this.defaultAnimatedDigitOffset - 1;
			element.style.transitionDuration = `${transitionDuration}s`;
			element.style.transform = `translateY(-${dist * 100}%)`;
			i++;
		}
	}

	private trimAndClearNewLines(value: string): string {
		const pattern = /\r?\n|\r/g;

		return value.trim().replace(pattern, '');
	}

	// Example: '$37,758' -> ["$", "37", ",", "758"]
	private splitNumbersAndNonNumbers(value: string): string[] {
		const pattern = /(\D+)|(\d+)|(\.)+|(,)+/g;

		return value?.match(pattern) || [];
	}

	private generateAnimatedHTML(digit: string): string {
		const tempSolution = Array.from(Array(this.defaultAnimatedDigitOffset)).map((_, idx) => {
			let value = parseInt(digit) - this.defaultAnimatedDigitOffset + (idx + 1);
			if (value < 0) {
				value = 10 - Math.abs(value);
			}

			return `<span>${value}</span>`;
		});

		return tempSolution.join('');
	}
}
